import { REACT_APP_BE_API_URL } from '../constants';

import { fetchWithAuthHeaders } from './fetchWithAuthHeaders';
import { recoverFromAuthFailure } from './handleSessionExpired';

import type { BaseQueryFn } from '@reduxjs/toolkit/query';

const FETCH_TIMEOUT = 100_000; // 100 seconds

/**
 * The base query flattens a thrown error into this plain object, discarding the
 * class. `name` is therefore the only surviving trace of WHICH error it was, and
 * that is how the `rtkQueryErrorLogger` seam recognizes an auth failure that has
 * already reported its own telemetry. It is required rather than optional so a
 * future producer cannot silently turn that seam into a no-op.
 */
export interface QueryError {
  error: string;
  name: string;
  status?: number;
  body?: unknown;
}

type AuthBaseQuery = BaseQueryFn<
  string,
  unknown,
  QueryError,
  unknown,
  { signal?: AbortSignal }
>;

const toQueryError = (err: unknown): { error: QueryError } => {
  if (!(err instanceof Error)) {
    return { error: { error: 'Unknown error', name: 'UnknownError' } };
  }
  if (!('body' in err)) {
    return { error: { error: err.message, name: err.name } };
  }
  const { status, body } = err as Error & { status?: unknown; body: unknown };
  return {
    error: {
      error: err.message,
      name: err.name,
      body,
      ...(typeof status === 'number' ? { status } : {}),
    },
  };
};

/**
 * Builds an RTK Query base query that prepends `baseUrl`, attaches auth headers,
 * and enforces a timeout + abort. Most APIs hit `REACT_APP_BE_API_URL`; the stats
 * API lives behind `REACT_APP_BE_API_STATS_URL`, hence the parameterization.
 */
export const createFetchWithAuthBaseQuery =
  (baseUrl: string): AuthBaseQuery =>
  async (url, api) => {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(
      () => controller.abort(),
      FETCH_TIMEOUT,
    );

    // Link to RTK Query's abort signal (for component unmount/cache invalidation)
    api.signal?.addEventListener('abort', () => controller.abort());

    try {
      const fullUrl = `${baseUrl}${url}`;
      const data = await fetchWithAuthHeaders(fullUrl, true, {
        signal: controller.signal,
      });
      return { data };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { error: { error: 'Request timed out', name: 'AbortError' } };
      }
      recoverFromAuthFailure(err);
      return toQueryError(err);
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  };

export const fetchWithAuthBaseQuery =
  createFetchWithAuthBaseQuery(REACT_APP_BE_API_URL);

/**
 * The human-readable half of a rejected query, whatever produced it.
 *
 * The base query and `asQueryFnResult` both flatten a thrown error to a plain
 * `QueryError`, so `instanceof` is gone by the time anything can render it and the
 * message is all that survives. RTK also rejects with shapes this module never
 * built (`unwrap()` on a serialised error), hence the narrowing.
 */
export const queryErrorMessage = (error: unknown): string | null => {
  if (typeof error === 'string') {
    return error;
  }
  if (error === null || typeof error !== 'object') {
    return null;
  }
  const { error: message } = error as { error?: unknown };
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  const { message: fallback } = error as { message?: unknown };
  return typeof fallback === 'string' && fallback.length > 0 ? fallback : null;
};

/**
 * Adapts a throwing async producer to RTK Query's `queryFn` result contract,
 * emitting the same `QueryError` shape as the base query — and running the same
 * session recovery, so a `queryFn` endpoint is not a hole in it. For endpoints
 * that can't use the JSON base query (raw Response, gz, header reads).
 */
export const asQueryFnResult = async <T>(produce: () => Promise<T>) => {
  try {
    return { data: await produce() };
  } catch (err) {
    recoverFromAuthFailure(err);
    return toQueryError(err);
  }
};
